import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { client } from '../../amplify-client';
import { GuennieRating } from '../../components/GuennieRating';
import { KriteriumZeile } from '../../components/KriteriumZeile';
import { SpaghettiDivider } from '../../components/SpaghettiDivider';
import { bildVerkleinern } from '../../lib/bildVerkleinern';
import { useFotoUrl } from '../../lib/useFotoUrl';
import { uebersetzeSchreibFehler } from '../../lib/fehler';
import { PFLICHT_KRITERIEN, OPTIONALE_KRITERIEN, type Kriterium } from '../../lib/kriterien';
import { berechneGesamtGuennies, pflichtnotenVollstaendig } from '../../../amplify/shared/guennies';
import type { Schema } from '../../../amplify/data/resource';

type Test = Schema['SpaghettiEisTest']['type'];
type Eisdiele = Schema['Eisdiele']['type'];

const HEUTE = new Date().toISOString().slice(0, 10);

/**
 * Verwaltung → Verkostung bearbeiten (Bauphase 7). Anders als
 * VerkostungBearbeiten.tsx (Tester-Selbstbedienung, nur eigene Entwürfe) gilt
 * hier: Verwalter dürfen JEDE Verkostung in JEDEM Status ändern, veröffentlichen,
 * zurückziehen oder löschen (`eisTestVerwalterAktualisieren` &Co. sind laut
 * Handler ausschließlich Verwalter-Aktionen bzw. prüfen `admin` zuerst).
 */
export function VerwaltungTestBearbeiten() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [test, setTest] = useState<Test | null>(null);
  const [eisdiele, setEisdiele] = useState<Eisdiele | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [variante, setVariante] = useState('');
  const [preis, setPreis] = useState('');
  const [serviertAls, setServiertAls] = useState('');
  const [wartezeit, setWartezeit] = useState('');
  const [besuchsdatum, setBesuchsdatum] = useState(HEUTE);
  const [noten, setNoten] = useState<Partial<Record<Kriterium, number>>>({});
  const [fazit, setFazit] = useState('');
  const [wiederkommen, setWiederkommen] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoVorschau, setFotoVorschau] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bestehendesFoto = useFotoUrl(test?.id, test?.fotoKey);

  useEffect(() => {
    if (!id) return;
    setLaedt(true);
    client.models.SpaghettiEisTest.get({ id })
      .then(async ({ data, errors }) => {
        if (errors?.length) throw new Error(errors[0].message);
        if (!data) {
          setLadeFehler('Diese Verkostung wurde nicht gefunden.');
          return;
        }
        setTest(data);
        setVariante(data.variante ?? '');
        setPreis(data.portionspreisEuro != null ? String(data.portionspreisEuro) : '');
        setServiertAls(data.serviertAls ?? '');
        setWartezeit(data.wartezeitMin != null ? String(data.wartezeitMin) : '');
        setBesuchsdatum(data.besuchsdatum ?? HEUTE);
        setFazit(data.fazit ?? '');
        setWiederkommen(data.wiederkommen ?? '');
        setNoten({
          noteSpaghettiOptik: data.noteSpaghettiOptik ?? undefined,
          noteKonsistenz: data.noteKonsistenz ?? undefined,
          noteGeschmack: data.noteGeschmack ?? undefined,
          noteFruchtsauce: data.noteFruchtsauce ?? undefined,
          noteWeisseRaspel: data.noteWeisseRaspel ?? undefined,
          noteSahne: data.noteSahne ?? undefined,
          notePortionsgroesse: data.notePortionsgroesse ?? undefined,
          notePraesentation: data.notePraesentation ?? undefined,
          notePreisLeistung: data.notePreisLeistung ?? undefined,
        });
        const { data: e } = await client.models.Eisdiele.get({ id: data.eisdieleId });
        setEisdiele(e ?? null);
      })
      .catch((e) => setLadeFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, [id]);

  const gesamt = useMemo(() => berechneGesamtGuennies(noten), [noten]);
  const pflichtOk = pflichtnotenVollstaendig(noten);

  function setzeNote(k: Kriterium, wert: number) {
    setNoten((n) => ({ ...n, [k]: wert }));
  }

  async function fotoGewaehlt(datei: File | null) {
    setFehler(null);
    if (!datei) {
      setFoto(null);
      setFotoVorschau(null);
      return;
    }
    try {
      const blob = await bildVerkleinern(datei);
      const f = new File([blob], 'foto.jpg', { type: 'image/jpeg' });
      setFoto(f);
      setFotoVorschau(URL.createObjectURL(f));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function felderSpeichern() {
    if (!test) throw new Error('NICHT_GEFUNDEN');
    let fotoKey = test.fotoKey ?? undefined;
    if (foto) {
      const { data: uploadUrl, errors: urlErrors } = await client.mutations.fotoUploadUrlAnfordern({
        testId: test.id,
        contentType: 'image/jpeg',
      });
      if (urlErrors?.length || !uploadUrl) {
        throw new Error(urlErrors?.[0]?.message ?? 'Konnte keine Upload-Adresse für das Foto anfordern.');
      }
      const hochladen = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: foto,
      });
      if (!hochladen.ok) throw new Error('Foto-Upload fehlgeschlagen.');
      fotoKey = `tests/${test.id}/foto.jpg`;
    }

    const { data, errors } = await client.mutations.eisTestVerwalterAktualisieren({
      id: test.id,
      besuchsdatum,
      variante: variante || undefined,
      portionspreisEuro: preis ? Number(preis) : undefined,
      serviertAls: serviertAls || undefined,
      wartezeitMin: wartezeit ? Number(wartezeit) : undefined,
      noteSpaghettiOptik: noten.noteSpaghettiOptik,
      noteKonsistenz: noten.noteKonsistenz,
      noteGeschmack: noten.noteGeschmack,
      noteFruchtsauce: noten.noteFruchtsauce,
      noteWeisseRaspel: noten.noteWeisseRaspel,
      noteSahne: noten.noteSahne,
      notePortionsgroesse: noten.notePortionsgroesse,
      notePraesentation: noten.notePraesentation,
      notePreisLeistung: noten.notePreisLeistung,
      fotoKey,
      fazit: fazit || undefined,
      wiederkommen: wiederkommen || undefined,
    });
    if (errors?.length || !data) throw new Error(errors?.[0]?.message ?? 'Speichern fehlgeschlagen.');
    setTest(data);
    return data;
  }

  async function speichern() {
    setBusy(true);
    setFehler(null);
    try {
      await felderSpeichern();
      navigate('/app/verwaltung/verkostungen');
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function veroeffentlichen() {
    if (!test) return;
    setBusy(true);
    setFehler(null);
    try {
      if (!pflichtOk) throw new Error('PFLICHTNOTEN_FEHLEN');
      await felderSpeichern();
      const { errors } = await client.mutations.eisTestVeroeffentlichen({ id: test.id });
      if (errors?.length) throw new Error(errors[0].message);
      navigate('/app/verwaltung/verkostungen');
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function zuruecknehmen() {
    if (!test) return;
    setBusy(true);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestZuruecknehmen({ id: test.id });
      if (errors?.length) throw new Error(errors[0].message);
      navigate('/app/verwaltung/verkostungen');
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function loeschen() {
    if (!test) return;
    if (!confirm('Diese Verkostung wirklich unwiderruflich löschen?')) return;
    setBusy(true);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestLoeschen({ id: test.id });
      if (errors?.length) throw new Error(errors[0].message);
      navigate('/app/verwaltung/verkostungen');
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  if (laedt) return <p className="label">Lädt …</p>;

  if (ladeFehler || !test) {
    return (
      <div>
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          {ladeFehler ?? 'Nicht gefunden.'}
        </p>
        <Link to="/app/verwaltung/verkostungen">← zurück zur Liste</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '46rem' }}>
      <p className="label">
        <Link to="/app/verwaltung/verkostungen">← Verwaltung · Verkostungen</Link>
      </p>
      <h2 style={{ marginTop: 0 }}>
        {eisdiele?.name ?? 'Unbekannte Eisdiele'} · von {test.testerName}
      </h2>
      <p style={{ color: 'var(--espresso-70)' }}>
        {eisdiele?.ort ?? ''} · Status: {test.status === 'VEROEFFENTLICHT' ? 'Veröffentlicht' : 'Entwurf'}
      </p>
      <SpaghettiDivider />

      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <h2>Bewertung</h2>
          <div style={{ textAlign: 'right' }}>
            <div className="label">Gesamt (automatisch)</div>
            <GuennieRating wert={gesamt} groesse="md" />
          </div>
        </div>

        {PFLICHT_KRITERIEN.map((k) => (
          <KriteriumZeile key={k} k={k} wert={noten[k]} onChange={(v) => setzeNote(k, v)} pflicht />
        ))}

        <details style={{ marginTop: '1rem' }} open={OPTIONALE_KRITERIEN.some((k) => noten[k] != null)}>
          <summary className="label" style={{ cursor: 'pointer' }}>
            Optionale Kriterien
          </summary>
          <div style={{ marginTop: '0.75rem' }}>
            {OPTIONALE_KRITERIEN.map((k) => (
              <KriteriumZeile key={k} k={k} wert={noten[k]} onChange={(v) => setzeNote(k, v)} />
            ))}
          </div>
        </details>
      </section>

      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <h2>Anrichten</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Besuchsdatum</label>
              <input type="date" value={besuchsdatum} max={HEUTE} onChange={(e) => setBesuchsdatum(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Portionspreis (€)</label>
              <input type="number" step="0.10" value={preis} onChange={(e) => setPreis(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Wartezeit (Min.)</label>
              <input type="number" value={wartezeit} onChange={(e) => setWartezeit(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Serviert als</label>
              <select value={serviertAls} onChange={(e) => setServiertAls(e.target.value)}>
                <option value="">–</option>
                <option value="TELLER">Teller</option>
                <option value="GLAS">Glas</option>
                <option value="BECHER">Becher</option>
                <option value="TO_GO">to go</option>
              </select>
            </div>
            <div style={{ flex: '1 1 12rem' }}>
              <label>Variante</label>
              <input value={variante} onChange={(e) => setVariante(e.target.value)} />
            </div>
          </div>
          <div>
            <label>Foto der Portion (optional, max. 5 MB)</label>
            {!fotoVorschau && bestehendesFoto ? (
              <img
                src={bestehendesFoto}
                alt="Aktuelles Foto"
                style={{ display: 'block', marginBottom: '0.5rem', maxWidth: '12rem', borderRadius: 'var(--radius)' }}
              />
            ) : null}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => fotoGewaehlt(e.target.files?.[0] ?? null)}
            />
            {fotoVorschau ? (
              <img
                src={fotoVorschau}
                alt="Neue Vorschau"
                style={{ marginTop: '0.5rem', maxWidth: '100%', borderRadius: 'var(--radius)' }}
              />
            ) : null}
          </div>
          <div>
            <label>Verkostungsnotiz</label>
            <textarea value={fazit} onChange={(e) => setFazit(e.target.value)} />
          </div>
          <div>
            <label>Wiederkommen?</label>
            <select value={wiederkommen} onChange={(e) => setWiederkommen(e.target.value)}>
              <option value="">–</option>
              <option value="JA">ja</option>
              <option value="VIELLEICHT">vielleicht</option>
              <option value="NEIN">nein</option>
            </select>
          </div>
        </div>
      </section>

      {fehler ? <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>{fehler}</p> : null}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primaer" disabled={busy} onClick={speichern}>
          Speichern
        </button>
        {test.status === 'ENTWURF' ? (
          <button className="btn btn-sekundaer" disabled={busy || !pflichtOk} onClick={veroeffentlichen}>
            Veröffentlichen
          </button>
        ) : (
          <button className="btn btn-sekundaer" disabled={busy} onClick={zuruecknehmen}>
            Zurückziehen
          </button>
        )}
        <button className="btn btn-sekundaer" disabled={busy} onClick={loeschen} style={{ color: 'var(--erdbeere)', marginLeft: 'auto' }}>
          Löschen
        </button>
      </div>
    </div>
  );
}
